import { Action, createReducer, on } from '@ngrx/store';
import * as configsActions from '../actions/config.actions';

import {
  ConfigsInterface,
  CustomerType,
  FeatureFlagsEnum,
  ProductPackages,
  Solution,
  StorageNameMapping,
} from '@twaice-fe/shared/models';
import { CustomerConfiguration } from '../../../types/customer-configuration';
// Technical slicing rather than domain slicing for store means module boundaries can't be enforced.
// eslint-disable-next-line twaice/no-types-import
import { LoadConfigSuccessPayload } from '../types/config.types';

export const CONFIGS_FEATURE_KEY = 'configs';

export interface RemoteState {
  isLoading: boolean;
  error: string | null;
}

export interface State extends ConfigsInterface {
  featureFlags: string[] | FeatureFlagsEnum[];
  solutions: Solution[];
  customerType?: CustomerType;
  baseUrl?: string;
  customerBk?: string;
  useCustomerNaming: boolean;
  storageNameMappings: { [key: string]: StorageNameMapping };
  customerConfiguration: CustomerConfiguration & RemoteState;
  productPackages: ProductPackages;
}

export const initialState: State = {
  featureFlags: [],
  solutions: [],
  useCustomerNaming: true,
  storageNameMappings: {} as { [key: string]: StorageNameMapping },
  customerConfiguration: {
    isLoading: false,
    error: null,
    logoUrl: null,
  },
  productPackages: {},
};

type ActionReducer<T = unknown> = (state: State, payload: T) => State;

const reduceFetchCustomerConfiguration: ActionReducer = (state) => ({
  ...state,
  customerConfiguration: { isLoading: true, error: null, logoUrl: null },
});

const reduceFetchCustomerConfigurationSuccess: ActionReducer<CustomerConfiguration> = (state, { logoUrl }) => ({
  ...state,
  customerConfiguration: { isLoading: false, error: null, logoUrl },
});

const reduceFetchCustomerConfigurationFailure: ActionReducer<{ error: string }> = (state, { error }) => ({
  ...state,
  customerConfiguration: { isLoading: false, error, logoUrl: null },
});

const reduceLoadConfigSuccess: ActionReducer<LoadConfigSuccessPayload> = (
  state,
  { solutions, featureFlags, customerType, baseUrl, customerBk, productPackages }
) => ({
  ...state,
  solutions: [...solutions],
  featureFlags: [...featureFlags],
  customerType,
  baseUrl,
  customerBk,
  productPackages: { ...productPackages },
});

const configReducer = createReducer(
  initialState,
  on(configsActions.loadFeatureFlagsSuccess, (state, { featureFlags }) => ({
    ...state,
    featureFlags: [...featureFlags],
  })),
  on(configsActions.loadSolutionConfigSuccess, (state, { solutions }) => ({
    ...state,
    solutions: [...solutions],
  })),
  on(configsActions.loadConfigSuccess, reduceLoadConfigSuccess),
  on(configsActions.useCustomerNamingChanged, (state, { useCustomerNaming }) => ({
    ...state,
    useCustomerNaming,
  })),
  on(configsActions.loadNameMappingSuccess, (state, { customerBk, systemBk, componentNameMapping, levelNameMapping }) => ({
    ...state,
    storageNameMappings: {
      ...state.storageNameMappings,
      ...{ [`${customerBk}/${systemBk}`]: { componentNameMapping, levelNameMapping } },
    },
  })),
  on(configsActions.fetchCustomerConfiguration, reduceFetchCustomerConfiguration),
  on(configsActions.fetchCustomerConfigurationSuccess, reduceFetchCustomerConfigurationSuccess),
  on(configsActions.fetchCustomerConfigurationError, reduceFetchCustomerConfigurationFailure)
);
export function reducer(state: State | undefined, action: Action) {
  return configReducer(state, action);
}
